/**
 * OfficialInfoSection — ajuste POR AGENTE do cadastro oficial do negócio
 * (officialInfoOverride, types 1.2.243 / Fase 3 do verificador de fonte,
 * 2026-08-25).
 *
 * O padrão vem da EMPRESA (endereço/telefone/site das Configurações). Este
 * ajuste existe para o agente que atende OUTRA unidade: campo preenchido
 * vence o da empresa; campo vazio herda. O cadastro resolvido entra no
 * prompt do agente (ele responde endereço/telefone com o dado real) e na
 * evidência do verificador de fonte — a raiz do incidente em que o agente
 * inventou endereço era exatamente o dado não existir em lugar nenhum.
 *
 * Semântica do formData: só grava campo NÃO-VAZIO; sem nenhum campo,
 * `officialInfoOverride` sai do payload (herda tudo da empresa).
 */

import React from 'react';
import { Input, Textarea } from '@/shared/components/ui';
import type { AIAgentOfficialInfoOverride } from '@troia-v3/types';
import type { CreateAIAgentRequest } from '../services/agentsApi';
import { SectionCard } from './SectionCard';

type FieldKey = keyof AIAgentOfficialInfoOverride;

const FIELDS: Array<{
  key: FieldKey;
  label: string;
  placeholder: string;
  multiline?: boolean;
}> = [
  {
    key: 'addressLine',
    label: 'Endereço',
    placeholder: 'Rua, número, bairro, cidade - UF, CEP (vazio = herda o da empresa)',
  },
  { key: 'phone', label: 'Telefone', placeholder: '(47) 3344-5566 (vazio = herda o da empresa)' },
  { key: 'email', label: 'E-mail', placeholder: 'contato@empresa.com.br (vazio = herda o da empresa)' },
  { key: 'website', label: 'Site', placeholder: 'https://empresa.com.br (vazio = herda o da empresa)' },
  {
    key: 'businessHoursText',
    label: 'Horário de atendimento',
    placeholder: 'Seg-Sex 9h às 18h, Sáb 9h às 13h',
  },
  {
    key: 'notes',
    label: 'Outros dados oficiais',
    placeholder: 'Chave Pix, CNPJ da unidade, estacionamento… (opcional)',
    multiline: true,
  },
];

interface OfficialInfoSectionProps {
  formData: Partial<CreateAIAgentRequest>;
  setFormData: React.Dispatch<React.SetStateAction<Partial<CreateAIAgentRequest>>>;
}

export function OfficialInfoSection({ formData, setFormData }: OfficialInfoSectionProps) {
  const override = formData.officialInfoOverride ?? {};

  const handleChange = (key: FieldKey, raw: string) => {
    setFormData((prev) => {
      const current: AIAgentOfficialInfoOverride = { ...(prev.officialInfoOverride ?? {}) };
      const value = raw.trimStart();
      if (value.trim().length > 0) current[key] = value;
      else delete current[key];

      if (Object.keys(current).length === 0) {
        // Sem nenhum ajuste: o campo sai do payload e o agente herda TUDO
        // da empresa (nunca enviar objeto vazio).
        const { officialInfoOverride: _removed, ...rest } = prev;
        void _removed;
        return rest;
      }
      return { ...prev, officialInfoOverride: current };
    });
  };

  return (
    <SectionCard
      icon="map-pin"
      title="Cadastro oficial do negócio"
      subtitle="Endereço, telefone e horário que o agente pode afirmar. Vazio = usa o cadastro da empresa; preencha apenas se este agente atende outra unidade."
    >
      <div className="space-y-4">
        {FIELDS.map((field) => (
          <div key={field.key}>
            <label className="block text-sm font-medium mb-1" style={{ color: 'var(--theme-text)' }}>
              {field.label}
            </label>
            {field.multiline ? (
              <Textarea
                value={override[field.key] ?? ''}
                onChange={(e) => handleChange(field.key, e.target.value)}
                placeholder={field.placeholder}
                rows={2}
                data-testid={`agent-official-info-${field.key}`}
              />
            ) : (
              <Input
                value={override[field.key] ?? ''}
                onChange={(e) => handleChange(field.key, e.target.value)}
                placeholder={field.placeholder}
                data-testid={`agent-official-info-${field.key}`}
              />
            )}
          </div>
        ))}
      </div>
    </SectionCard>
  );
}
